import type { Database } from "@/integrations/supabase/types";
import type { RequestCategory, RequestLane } from "@/types/requests";

type DbRequestCategory = Database["public"]["Enums"]["request_type"];

type ReimbursementHints = {
  reimbursement_amount?: number | null;
  reimbursement_vendor?: string | null;
  reimbursement_purchase_date?: string | null;
  expense_category_id?: string | null;
  reimbursement_payment_method?: string | null;
};

const UI_TO_DB_CATEGORY_MAP: Record<RequestCategory, DbRequestCategory[]> = {
  care: ["pastoral_care", "benevolence"],
  facility: ["facility"],
  admin: ["tech", "volunteer"],
  reimbursement: ["other"],
  other: ["other"],
};

const isReimbursementLike = (row?: ReimbursementHints | null) => {
  if (!row) return false;
  return Boolean(
    row.reimbursement_amount != null ||
      row.reimbursement_vendor ||
      row.reimbursement_purchase_date ||
      row.expense_category_id ||
      row.reimbursement_payment_method
  );
};

export function toDbRequestCategory(
  category: string,
  options?: { careType?: string | null }
): DbRequestCategory {
  switch (category) {
    // Already DB enum values
    case "pastoral_care":
    case "facility":
    case "tech":
    case "benevolence":
    case "volunteer":
    case "other":
      return category;
    // Legacy UI values
    case "care":
      return options?.careType === "benevolence" ? "benevolence" : "pastoral_care";
    case "admin":
      return "tech";
    case "reimbursement":
      return "other";
    default:
      return "other";
  }
}

export function toDbRequestCategories(categories: RequestCategory[]): DbRequestCategory[] {
  const values = new Set<DbRequestCategory>();
  for (const category of categories) {
    for (const mapped of UI_TO_DB_CATEGORY_MAP[category] ?? ["other"]) {
      values.add(mapped);
    }
  }
  return [...values];
}

/**
 * Lane that owns each UI request category on the requests list page
 * (`/requests?lane=…`). Mirrors the routing in RequestRedirect.tsx —
 * keep the two in sync if lanes ever change.
 */
export const REQUEST_LANE_BY_CATEGORY: Record<RequestCategory, RequestLane> = {
  care: "care",
  reimbursement: "finance",
  facility: "operations",
  admin: "operations",
  other: "operations",
};

/** Resolve the requests-list lane for a request's (UI or DB) category. */
export function toRequestLane(
  category: string | null | undefined,
  row?: ReimbursementHints | null
): RequestLane {
  return REQUEST_LANE_BY_CATEGORY[toUiRequestCategory(category, row)];
}

export function toUiRequestCategory(
  category: string | null | undefined,
  row?: ReimbursementHints | null
): RequestCategory {
  switch (category) {
    case "care":
    case "pastoral_care":
    case "benevolence":
      return "care";
    case "facility":
      return "facility";
    case "admin":
    case "tech":
    case "volunteer":
      return "admin";
    case "reimbursement":
      return "reimbursement";
    case "other":
      return isReimbursementLike(row) ? "reimbursement" : "other";
    default:
      return "other";
  }
}
